/**
 * CollapsibleGroup — the accessible disclosure primitive the grouped launch
 * form is built from (plan Phase 3 task 1, AC-1).
 *
 * Two properties make "collapsing loses nothing" STRUCTURAL rather than
 * incidental, and both are invariants, not implementation details:
 *
 *  1. **No form value lives inside this component.** Every value the AM types is
 *     owned by the drawer's `values` state, one level above `LaunchForm`. Even if
 *     the collapsed subtree were unmounted, nothing typed could be lost. (It is
 *     not unmounted either — MUI's `Collapse` keeps the body mounted because
 *     `unmountOnExit` is deliberately NOT set — but that is the belt, not the
 *     braces.)
 *  2. **Collapsing issues no request.** There is no `useEffect` here at all, so
 *     no toggle can trigger a fetch, a poll or a re-extraction. The probe
 *     asserts zero network calls across a collapse+expand; this component is
 *     what makes that assertion structural.
 *
 * Accessibility (DoD, WCAG 2.1 AA): the header is a real
 * `<button type="button">` — keyboard operable and focus-visible by default —
 * carrying `aria-expanded` and `aria-controls`. `type="button"` is mandatory:
 * an unset type inside a `<form>` submits it, and this control renders inside a
 * launch form whose one submit button must be the only way to launch.
 *
 * Pattern source: the (since removed, story 28633) Epic sign-in disclosure in
 * `InstanceDetailDrawer.tsx` — same button/aria shape, lifted here so both hosts
 * share one disclosure.
 */

import { useId, useState, type ReactNode } from "react";
import { Collapse } from "@mui/material";
import {
  TYPE_BODY,
  TYPE_LABEL,
  TYPE_TITLE,
} from "~/common/workflows/paneTypography";

export interface CollapsibleGroupProps {
  /** Rendered as the header's primary line — the thing the group IS. */
  title: ReactNode;
  /**
   * Rendered under the title ONLY while the group is collapsed, so an AM can
   * still identify the group without expanding it. Hidden when open — the body
   * already shows the same facts, and a second copy under an open header is
   * noise. The policy group puts the term-precise identity here (PRD UI/UX).
   */
  summary?: ReactNode;
  /** AD-5: both of the form's collapse controls default to EXPANDED. */
  defaultExpanded?: boolean;
  /** Test hook. `<testId>-toggle`, `-summary` and `-body` are derived from it. */
  testId?: string;
  /**
   * `card` (default): a bordered card with a TYPE_TITLE header. `flat`: a
   * section INSIDE another group — a divider above a TYPE_LABEL header and no
   * border of its own, so a group nested in a card is not a second card
   * (story 28633: the "Mortgagee action" inside "Policy").
   */
  variant?: "card" | "flat";
  children: ReactNode;
}

export default function CollapsibleGroup({
  title,
  summary,
  defaultExpanded = true,
  testId,
  variant = "card",
  children,
}: CollapsibleGroupProps) {
  const flat = variant === "flat";
  const [expanded, setExpanded] = useState(defaultExpanded);
  // `useId` yields ":r0:"-style ids; strip the colons so the value is a legal
  // CSS selector for anything that queries by id.
  const bodyId = `launch-collapse-${useId().replace(/:/g, "")}`;

  return (
    <div
      className={
        flat
          ? "border-t border-primary-gray-light-200"
          : "rounded-lg border border-primary-gray-light-200 bg-base-white"
      }
      data-testid={testId}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((v) => !v)}
        className={`flex touch-target w-full items-start justify-between gap-2 text-left ${
          flat ? "py-1.5" : "rounded-lg px-3 py-1.5"
        }`}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="flex min-w-0 flex-col">
          <span
            className={`truncate ${
              flat
                ? `${TYPE_LABEL} text-primary-gray-light-600`
                : `${TYPE_TITLE} text-primary-gray-light-900`
            }`}
          >
            {title}
          </span>
          {summary && !expanded ? (
            <span
              className={`truncate ${TYPE_BODY} text-primary-gray-light-700`}
              data-testid={testId ? `${testId}-summary` : undefined}
            >
              {summary}
            </span>
          ) : null}
        </span>
        <span aria-hidden="true" className="flex-shrink-0 text-primary-gray-light-500">
          {expanded ? "▾" : "▸"}
        </span>
      </button>
      <Collapse in={expanded} timeout="auto">
        <div
          id={bodyId}
          className={flat ? "flex flex-col gap-2 pb-0.5" : "flex flex-col gap-2 px-3 pb-2.5"}
          data-testid={testId ? `${testId}-body` : undefined}
        >
          {children}
        </div>
      </Collapse>
    </div>
  );
}
